<template>
  <v-modal
    v-if="showModal || isPresent(currentForm)"
    :disable-close="disableClose"
    @close="handleClose"
    modal-class="form-modal"
  >
    <template :slot="FORM_MODAL_HASH">
      <div class="form-modal__content">
        <template v-if="isLoading">
          <div class="d-flex justify-content-center pt-5">
            <v-loader />
          </div>
        </template>
        <template v-else-if="isPresent(currentForm)">
          <form-template
            v-model="currentValues"
            :id="currentForm.id"
            :title="currentForm.title"
            :groups="filteredGroups"
            :trigger="currentForm.trigger"
            :triggeringItem="triggeringItem"
            :submittedHeading="currentForm.submitted_heading"
            :submittedDescription="currentForm.submitted_description"
            :showIntroScreen="showIntroScreen"
            :introHeading="currentForm.intro_heading"
            :introDescription="currentForm.intro_description"
            :show-group-uid="groupUid"
            :show-field-uid="itemUid"
            :active-group-index="activeGroupIndex"
            :has-continue-action="isPresent(continueAction)"
            :is-missable="isMissable"
            @submit="() => (isSubmitted = true)"
            @close="handleCloseForm"
          />
        </template>
      </div>
    </template>
  </v-modal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"

import { isPresent } from "@/lib/utils"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { generateFormGroups } from "@/lib/form-utils"
import { addToHash, removeFromHash } from "@/lib/url-helpers"

export const FORM_MODAL_HASH = "#form"

export default {
  components: {
    FormTemplate: () => import("@/components/page-templates/FormTemplate")
  },

  data() {
    return {
      currentForm: null,
      triggeringItem: null,
      canShowForm: false,
      showModal: false,
      isLoading: false,
      isSubmitted: false,
      isMissable: false,
      disableClose: false,

      itemUid: null,
      groupUid: null,
      overriddenItemTitle: null,
      activeGroupIndex: 0,
      continueAction: null,

      FORM_MODAL_HASH
    }
  },

  mounted() {
    // TODO: off other listeners too
    // TODO: Make trigger form more reliable when being triggered
    // when there is a hash already present
    //
    // Often you have to wrap the caller in a setTimeout to get
    // the form to reliably trigger
    //
    // Suggestion: Some internal queue, where this event adds a form to
    // the queue, and some watcher monitors $route.hash and figures out
    // when it can show the next form in the queue.
    EventBus.$on(
      EVENTS.TRIGGER_FORM,
      ({ formId, continueAction, disableClose = false }) => {
        this.fetchAndShowForm(formId)
        this.continueAction = continueAction
        this.disableClose = disableClose
      }
    )

    EventBus.$on(EVENTS.WORKOUT_COMPLETED, ({ formId, workoutId }) => {
      const triggeringItem = { itemType: "Workout", itemId: workoutId }

      this.fetchAndShowForm(formId, triggeringItem, true)
    })

    EventBus.$on(EVENTS.FORM_CAPTURE_PROGRESS, this.handleFormCaptureProgress)
  },

  beforeDestroy() {
    this.continueAction = null
    EventBus.$off(EVENTS.FORM_CAPTURE_PROGRESS, this.handleFormCaptureProgress)

    if (EventBus.hasListener(EVENTS.POSTING_SIGN_UP_ACTION)) {
      EventBus.$off(EVENTS.POSTING_SIGN_UP_ACTION)
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("forms", ["formById", "submissionValuesByForm"]),

    showIntroScreen() {
      return this.isPartialCapture ? false : this.currentForm.show_intro_screen
    },

    isPartialCapture() {
      return this.groupUid || this.itemUid
    },

    formId() {
      return this.currentForm?.id
    },

    formValues() {
      return this.submissionValuesByForm(this.formId)
    },

    currentValues: {
      get() {
        return this.formValues
      },

      set(values) {
        for (const fieldId in values) {
          this.updateFormSubmissionValue({
            formId: this.formId,
            fieldId,
            data: {
              ...(this.formValues[fieldId] || {}),
              ...(values[fieldId] || {})
            }
          })
        }
      }
    },

    filteredGroups() {
      return generateFormGroups(
        this.groups,
        this.groupUid,
        this.itemUid,
        this.overriddenItemTitle
      )
    },

    groups() {
      return this.currentForm.form_groups
    }
  },

  methods: {
    isPresent,

    ...mapActions("forms", ["fetchForm"]),
    ...mapActions("analytics", ["trackFormViewed", "trackFormAborted"]),
    ...mapMutations("forms", ["updateFormSubmissionValue"]),

    fetchAndShowForm(formId, triggeringItem = null, isMissable = false) {
      this.$router.push(addToHash(this.$route.hash, FORM_MODAL_HASH))
      this.showModal = true
      this.isMissable = isMissable

      const savedForm = this.formById(formId)

      if (isPresent(savedForm) && isPresent(savedForm.form_groups)) {
        this.setCurrentForm(savedForm, triggeringItem)
        this.trackFormViewed({
          form: this.currentForm,
          triggeringItem: this.triggeringItem
        })
      } else {
        this.isLoading = true

        this.fetchForm(formId)
          .then(form => {
            this.setCurrentForm(form, triggeringItem)
            this.trackFormViewed({
              form: this.currentForm,
              triggeringItem: this.triggeringItem
            })
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },

    setCurrentForm(form, triggeringItem) {
      this.isSubmitted = false
      this.currentForm = form
      this.triggeringItem = triggeringItem
    },

    resetModal() {
      this.resetState()
      this.$router.push(removeFromHash(this.$route.hash, FORM_MODAL_HASH))

      EventBus.$emit(EVENTS.FORM_CLOSED, this.currentForm?.id)
    },

    resetState() {
      this.currentForm = null
      this.groupUid = null
      this.itemUid = null
      this.overriddenItemTitle = null
      this.canShowForm = false
      this.activeGroupIndex = 0
    },

    handleClose() {
      if (!this.isSubmitted && !this.isPartialCapture)
        this.trackFormAborted({ form: this.currentForm })

      this.triggerContinueAction()
      this.resetState()

      EventBus.$emit(EVENTS.FORM_CLOSED, this.currentForm?.id)
    },

    handleCloseForm() {
      this.resetModal()
      this.triggerContinueAction()
    },

    triggerContinueAction() {
      if (isPresent(this.continueAction) && this.isSubmitted) {
        EventBus.$emit(EVENTS.TRIGGER_BLOCK_ACTION, this.continueAction)
      }
    },

    handleFormCaptureProgress({
      formId,
      groupUid,
      overriddenItemTitle,
      itemUid,
      startIndex
    }) {
      this.groupUid = groupUid
      this.overriddenItemTitle = overriddenItemTitle
      this.itemUid = itemUid
      this.activeGroupIndex = startIndex || 0
      const isMissable = true

      this.fetchAndShowForm(formId, null, isMissable)
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.form-modal {
  // changed border radius, as there is progress bar at the top
  .v-modal__content {
    padding-bottom: 10vh;
    border-top-left-radius: $size--2;
    border-top-right-radius: $size--2;
  }

  .v-modal__content-wrapper {
    border-top-left-radius: $size--2;
    border-top-right-radius: $size--2;
    overflow: hidden; // hide here because there is close button
  }
}
</style>
